/**
 * <bk-hero> — банер-слайдер із автопрокруткою і прогрес-баром у пагінації.
 *
 * Прогресивне покращення: без скрипта стрічка гортається пальцем (scroll-snap),
 * а стрілки й крапки просто нічого не роблять.
 *
 * Таймінг автопрокрутки задає не setTimeout, а сама CSS-анімація заливки
 * активної крапки (`--bk-hero-delay`): скрипт слухає її `animationend`.
 * Завдяки цьому смужка й перемикання завжди йдуть синхронно, а пауза
 * (`data-paused`) — це просто `animation-play-state: paused` у CSS.
 */
class BkHero extends HTMLElement {
  #viewport = null;
  #slides = [];
  #dots = [];
  #index = 0;
  #frame = 0;

  connectedCallback() {
    this.#viewport = this.querySelector('[data-bk-hero-viewport]');
    if (!this.#viewport) return;

    this.#slides = [...this.querySelectorAll('[data-bk-hero-slide]')];
    this.#dots = [...this.querySelectorAll('[data-bk-hero-dot]')];

    this.#viewport.addEventListener('scroll', this.#onScroll, { passive: true });
    this.addEventListener('animationend', this.#onProgressEnd);
    this.addEventListener('pointerenter', this.#pause);
    this.addEventListener('pointerleave', this.#resume);
    this.addEventListener('focusin', this.#pause);
    this.addEventListener('focusout', this.#resume);
    document.addEventListener('visibilitychange', this.#onVisibilityChange);

    this.querySelector('[data-bk-hero-prev]')?.addEventListener('click', this.#handlePrev);
    this.querySelector('[data-bk-hero-next]')?.addEventListener('click', this.#handleNext);
    this.#dots.forEach((dot, index) => {
      dot.addEventListener('click', () => this.#goTo(index));
    });

    this.#sync();
  }

  disconnectedCallback() {
    this.#viewport?.removeEventListener('scroll', this.#onScroll);
    this.removeEventListener('animationend', this.#onProgressEnd);
    this.removeEventListener('pointerenter', this.#pause);
    this.removeEventListener('pointerleave', this.#resume);
    this.removeEventListener('focusin', this.#pause);
    this.removeEventListener('focusout', this.#resume);
    document.removeEventListener('visibilitychange', this.#onVisibilityChange);
    cancelAnimationFrame(this.#frame);
  }

  #handlePrev = () => this.#goTo(this.#index - 1);
  #handleNext = () => this.#goTo(this.#index + 1);

  #pause = () => {
    this.dataset.paused = 'true';
  };

  #resume = () => {
    delete this.dataset.paused;
  };

  #onVisibilityChange = () => {
    if (document.hidden) this.#pause();
    else this.#resume();
  };

  /**
   * Крок стрічки — ширина слайда плюс просвіт між банерами. Беремо з реальної
   * різниці позицій, щоб не дублювати в скрипті значення `gap` із CSS.
   * @returns {number}
   */
  #pitch() {
    const viewport = this.#viewport;
    if (!viewport) return 0;

    const [first, second] = this.#slides;
    if (first && second) return second.offsetLeft - first.offsetLeft;
    return viewport.clientWidth;
  }

  /**
   * Прокрутка до слайда; індекс закільцьований в обидва боки.
   * @param {number} index
   */
  #goTo(index) {
    const viewport = this.#viewport;
    if (!viewport || this.#slides.length === 0) return;

    const total = this.#slides.length;
    const target = ((index % total) + total) % total;
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    viewport.scrollTo({
      left: target * this.#pitch(),
      behavior: reduceMotion ? 'auto' : 'smooth',
    });
  }

  /** Скрол задає стан: індекс беремо з позиції стрічки, а не з наміру. */
  #onScroll = () => {
    cancelAnimationFrame(this.#frame);
    this.#frame = requestAnimationFrame(this.#sync);
  };

  #sync = () => {
    const viewport = this.#viewport;
    if (!viewport) return;

    const pitch = this.#pitch();
    if (pitch === 0) return;

    const index = Math.round(viewport.scrollLeft / pitch);
    if (index === this.#index && this.#dots[index]?.getAttribute('aria-current') === 'true') return;

    this.#index = index;
    this.#dots.forEach((dot, i) => {
      // Зняття й повернення атрибута перезапускає CSS-анімацію заливки,
      // тобто відлік до наступного слайда завжди починається заново.
      if (i === index) dot.setAttribute('aria-current', 'true');
      else dot.removeAttribute('aria-current');
    });
  };

  /** Заливка добігла кінця — час наступного слайда. */
  #onProgressEnd = (/** @type {AnimationEvent} */ event) => {
    if (!(event.target instanceof Element) || !event.target.hasAttribute('data-bk-hero-progress')) return;
    this.#handleNext();
  };
}

if (!customElements.get('bk-hero')) {
  customElements.define('bk-hero', BkHero);
}
